/**
 * `SignaturePad` — canvas de signature réutilisable (souris + tactile,
 * retina-crisp, avec bouton « Effacer »).
 *
 * Extrait du flow de signature contrat pour être partagé avec le module
 * Bon de Remise Électronique (iter110n · CEO 2026-09-27).
 *
 * Props :
 *   - onChange(dataUrl, empty)  : callback à chaque interaction
 *   - disabled                  : désactive tout (ex: après validation)
 *
 * Retourne un data URI PNG utilisable directement dans l'API backend.
 */
import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";

export default function SignaturePad({ onChange, disabled, testIdPrefix = "signature" }) {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastPtRef = useRef(null);
    const [empty, setEmpty] = useState(true);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        const ctx = canvas.getContext("2d");
        ctx.scale(dpr, dpr);
        ctx.lineWidth = 2.4;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#1B1B1B";
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, rect.width, rect.height);
    }, []);

    const eventToPoint = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        if (e.touches && e.touches[0]) {
            return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
        }
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const start = (e) => {
        if (disabled) return;
        e.preventDefault();
        drawingRef.current = true;
        lastPtRef.current = eventToPoint(e);
    };
    const move = (e) => {
        if (!drawingRef.current || disabled) return;
        e.preventDefault();
        const p = eventToPoint(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(lastPtRef.current.x, lastPtRef.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        lastPtRef.current = p;
        if (empty) setEmpty(false);
    };
    const end = () => {
        if (!drawingRef.current) return;
        drawingRef.current = false;
        lastPtRef.current = null;
        const dataUrl = canvasRef.current.toDataURL("image/png");
        onChange?.(dataUrl, false);
    };
    const clear = () => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, rect.width, rect.height);
        setEmpty(true);
        onChange?.(null, true);
    };

    return (
        <div className="rounded-2xl bg-white border-2 border-slate-200 p-1.5">
            <canvas
                ref={canvasRef}
                data-testid={`${testIdPrefix}-canvas`}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                className={`w-full h-40 rounded-xl bg-white cursor-crosshair touch-none ${disabled ? "opacity-50" : ""}`}
                style={{ display: "block" }}
            />
            <div className="flex items-center justify-between px-3 py-1.5">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {empty ? "Signez ici (doigt ou souris)" : "Signature capturée ✓"}
                </div>
                <button
                    type="button"
                    onClick={clear}
                    disabled={disabled || empty}
                    data-testid={`${testIdPrefix}-clear`}
                    className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-widest text-slate-500 hover:text-red-600 transition disabled:opacity-40"
                >
                    <Eraser className="h-3 w-3" /> Effacer
                </button>
            </div>
        </div>
    );
}
